"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";
import type { CaptchaResponse, CurrentUserResponse, LoginRequest } from "./types";

/** 当前登录用户查询 key，供登录/登出后 invalidate。 */
export const authUserKey = ["auth", "me"] as const;

/** 登录验证码配置；启用时每次取新码（staleTime 0，强制刷新）。 */
export function useCaptcha() {
  return useQuery<CaptchaResponse, Error>({
    queryKey: ["auth", "captcha"],
    queryFn: () => fetchJson<CaptchaResponse>("/auth/captcha"),
    staleTime: 0,
    retry: false,
  });
}

/** 登录：成功后失效当前用户缓存，由调用方负责跳转。 */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<CurrentUserResponse, Error, LoginRequest>({
    mutationFn: (req) =>
      fetchJson<CurrentUserResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authUserKey });
    },
  });
}

/** 登出：清空所有缓存并跳登录页。 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => fetchJson<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => {
      queryClient.clear();
      window.location.assign("/login");
    },
  });
}

/** 当前登录用户；未登录时 401（fetchJson 会跳登录页）。 */
export function useCurrentUser() {
  return useQuery<CurrentUserResponse, Error>({
    queryKey: authUserKey,
    queryFn: () => fetchJson<CurrentUserResponse>("/auth/me"),
    retry: false,
    staleTime: 60_000,
  });
}
